import {
  clampMenuToViewport,
  ContextMenuItem,
  ContextMenuSurface,
  KebabButton,
} from '@src/components/common/contextMenuStyles';
import DeleteIcon from '@src/svgs/DeleteIcon';
import LinkIcon from '@src/svgs/LinkIcon';
import MoreVertIcon from '@src/svgs/MoreVertIcon';
import PencilIcon from '@src/svgs/PencilIcon';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useOutsideAlerter } from '../../transcript/useClickOutside';

import type { CSSProperties, FC, MouseEvent, RefObject } from 'react';

type Props = {
  onCopyLink: () => void;
  onRename: () => void;
  onDelete: () => void;
  contextMenuPos?: { x: number; y: number } | null;
  onCloseContextMenu?: () => void;
};

type MenuItemsProps = {
  onCopyLink: () => void;
  onRename: () => void;
  onDelete: () => void;
};

const MENU_WIDTH = 180;
// Three rows. Feeds `clampMenuToViewport`, so it must track the row count —
// an understated height stops the menu flipping near the viewport edge.
const MENU_HEIGHT = 132;

const FolderMenuItems: FC<MenuItemsProps> = ({
  onCopyLink,
  onRename,
  onDelete,
}) => {
  const stop = (cb: () => void) => (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    cb();
  };

  return (
    <>
      {/* Read-only action leads; the two that change the folder follow. */}
      <ContextMenuItem
        onClick={stop(onCopyLink)}
        data-testid="folder-context-copy-link"
      >
        <LinkIcon strokeColor="currentColor" /> Copy Link
      </ContextMenuItem>
      <ContextMenuItem
        onClick={stop(onRename)}
        data-testid="folder-context-rename"
      >
        <PencilIcon strokeColor="currentColor" /> Rename
      </ContextMenuItem>
      <ContextMenuItem
        $danger
        onClick={stop(onDelete)}
        data-testid="folder-context-delete"
      >
        <DeleteIcon /> Delete
      </ContextMenuItem>
    </>
  );
};

const FolderContextMenu: FC<Props> = ({
  onCopyLink,
  onRename,
  onDelete,
  contextMenuPos,
  onCloseContextMenu,
}) => {
  const [kebabMenuPos, setKebabMenuPos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const kebabPortalRef = useRef<HTMLDivElement>(null);
  const ctxPortalRef = useRef<HTMLDivElement>(null);

  const closeKebab = useCallback(() => setKebabMenuPos(null), []);
  useOutsideAlerter(kebabPortalRef, closeKebab);
  useOutsideAlerter(ctxPortalRef, () => onCloseContextMenu?.());

  useEffect(() => {
    if (!kebabMenuPos && !contextMenuPos) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (kebabMenuPos) closeKebab();
      if (contextMenuPos) onCloseContextMenu?.();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [kebabMenuPos, contextMenuPos, closeKebab, onCloseContextMenu]);

  const handleKebabClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    if (kebabMenuPos) {
      closeKebab();
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    // Anchor menu under the kebab, right-aligned.
    setKebabMenuPos(
      clampMenuToViewport(
        rect.right - MENU_WIDTH,
        rect.bottom + 4,
        MENU_WIDTH,
        MENU_HEIGHT,
      ),
    );
  };

  const wrappedCopyLink = () => {
    closeKebab();
    onCloseContextMenu?.();
    onCopyLink();
  };

  const wrappedRename = () => {
    closeKebab();
    onCloseContextMenu?.();
    onRename();
  };

  const wrappedDelete = () => {
    closeKebab();
    onCloseContextMenu?.();
    onDelete();
  };

  const renderFloating = (
    pos: { x: number; y: number },
    ref: RefObject<HTMLDivElement>,
    testId: string,
  ) => {
    const style: CSSProperties = { top: pos.y, left: pos.x };
    return createPortal(
      <ContextMenuSurface ref={ref} style={style} data-testid={testId}>
        <FolderMenuItems
          onCopyLink={wrappedCopyLink}
          onRename={wrappedRename}
          onDelete={wrappedDelete}
        />
      </ContextMenuSurface>,
      document.body,
    );
  };

  return (
    <>
      <KebabButton
        ref={buttonRef}
        onClick={handleKebabClick}
        data-testid="folder-context-menu-trigger"
        aria-label="Folder actions"
      >
        <MoreVertIcon />
      </KebabButton>
      {kebabMenuPos &&
        renderFloating(kebabMenuPos, kebabPortalRef, 'folder-context-menu')}
      {contextMenuPos &&
        renderFloating(
          clampMenuToViewport(
            contextMenuPos.x,
            contextMenuPos.y,
            MENU_WIDTH,
            MENU_HEIGHT,
          ),
          ctxPortalRef,
          'folder-context-menu-floating',
        )}
    </>
  );
};

export default FolderContextMenu;
